import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Calendar, TrendingUp, CheckCircle, Clock, Star, MessageSquare, Building, User, FileText } from 'lucide-react';
import { toast } from 'sonner';
import ContractWorkflowSection from '../components/ContractWorkflowSection';
import CollaborationMessages from '../components/CollaborationMessages';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CollaborationsPage = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [collaborations, setCollaborations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, in_progress, completed
  const [selectedCollab, setSelectedCollab] = useState(null);
  const [viewingCollab, setViewingCollab] = useState(null);
  const [activeTab, setActiveTab] = useState('overview'); // overview, contract, messages
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [updateData, setUpdateData] = useState({
    progress_status: '',
    progress_notes: ''
  });
  const [ratingData, setRatingData] = useState({
    score: 0,
    comment: ''
  });

  useEffect(() => {
    fetchCollaborations();
  }, []);

  const fetchCollaborations = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/collaborations/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCollaborations(response.data);
    } catch (error) {
      console.error('Error fetching collaborations:', error);
      toast.error('Failed to load collaborations');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProgress = async () => {
    if (!selectedCollab) return;

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/collaborations/${selectedCollab.collaboration.id}`,
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Progress updated successfully');
      setShowUpdateModal(false);
      fetchCollaborations();
    } catch (error) {
      console.error('Error updating:', error);
      toast.error('Failed to update progress');
    }
  };

  const handleCompleteCollaboration = async (collabId) => {
    if (!window.confirm('Mark this collaboration as completed?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/collaborations/${collabId}`,
        { status: 'completed', actual_end_date: new Date().toISOString().split('T')[0] },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Collaboration marked as completed');
      fetchCollaborations();
    } catch (error) {
      console.error('Error completing:', error);
      toast.error('Failed to complete collaboration');
    }
  };

  const handleSubmitRating = async () => {
    if (!selectedCollab || ratingData.score === 0) {
      toast.error('Please select a rating');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/collaborations/${selectedCollab.collaboration.id}/rate`,
        ratingData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Rating submitted successfully');
      setShowRatingModal(false);
      setRatingData({ score: 0, comment: '' });
      fetchCollaborations(); // Refresh data after rating
    } catch (error) {
      console.error('Error submitting rating:', error);
      const errorMessage = typeof error.response?.data?.detail === 'string' 
        ? error.response.data.detail 
        : 'Failed to submit rating';
      toast.error(errorMessage);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      in_progress: 'bg-blue-100 text-blue-800 border-blue-300',
      completed: 'bg-green-100 text-green-800 border-green-300',
      cancelled: 'bg-red-100 text-red-800 border-red-300'
    };

    const icons = {
      pending: <Clock className="w-3 h-3 mr-1" />,
      in_progress: <TrendingUp className="w-3 h-3 mr-1" />,
      completed: <CheckCircle className="w-3 h-3 mr-1" />,
      cancelled: null
    };

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full border flex items-center ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
        {icons[status]}
        {status.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  const filteredCollaborations = collaborations.filter(c => {
    if (filter === 'all') return true;
    return c.collaboration.status === filter;
  });

  const isPM = user?.role === 'project_manager';

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#62D84E]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold mb-2" style={{ color: '#1B3240' }}>
              My Collaborations
            </h1>
            <p className="text-lg text-gray-600">
              Track and manage your ongoing and completed collaborations
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6 flex items-center gap-4">
          <Label>Filter:</Label>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Collaborations</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-sm text-gray-600">
            {filteredCollaborations.length} collaboration(s)
          </span>
        </div>

      {/* Collaborations List */}
      {filteredCollaborations.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <MessageSquare className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">No collaborations found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6">
          {filteredCollaborations.map((item) => (
            <Card key={item.collaboration.id} className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      {getStatusBadge(item.collaboration.status)}
                      {item.collaboration.progress_status && (
                        <span className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded">
                          Progress: {item.collaboration.progress_status}
                        </span>
                      )}
                    </div>
                    
                    {/* Project Info */}
                    <div className="mb-3">
                      <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                        <Building className="w-4 h-4" />
                        <span className="font-medium">Project:</span>
                      </div>
                      <Link 
                        to={`/projects/${item.project?.id}`}
                        className="text-lg font-semibold text-[#1B3240] ml-6 hover:text-blue-600 hover:underline cursor-pointer"
                      >
                        {item.project?.title || 'Unknown Project'}
                      </Link>
                      {item.pm_company && (
                        <Link 
                          to={`/company/${item.pm_company.id}`}
                          className="text-sm text-gray-600 ml-6 block hover:text-blue-600 hover:underline"
                        >
                          {item.pm_company.name}
                        </Link>
                      )}
                    </div>

                    {/* Resource Info */}
                    <div className="mb-3">
                      <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                        <User className="w-4 h-4" />
                        <span className="font-medium">Resource:</span>
                      </div>
                      <Link
                        to={`/resources/${item.resource?.id}`}
                        className="text-lg font-semibold text-[#1B3240] ml-6 hover:text-blue-600 hover:underline cursor-pointer"
                      >
                        {item.resource?.first_name} {item.resource?.last_name}
                      </Link>
                      {item.rm_company && (
                        <Link 
                          to={`/company/${item.rm_company.id}`}
                          className="text-sm text-gray-600 ml-6 block hover:text-blue-600 hover:underline"
                        >
                          {item.rm_company.name}
                        </Link>
                      )}
                    </div>

                    {/* Timeline */}
                    <div className="flex items-center gap-4 text-sm text-gray-600 mt-3">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        <span>Started: {item.collaboration.start_date ? new Date(item.collaboration.start_date).toLocaleDateString() : 'Not started'}</span>
                      </div>
                      {item.collaboration.estimated_end_date && (
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          <span>Est. End: {new Date(item.collaboration.estimated_end_date).toLocaleDateString()}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-1">
                        <span className="font-medium">{item.collaboration.commitment_percentage}%</span>
                        <span>commitment</span>
                      </div>
                    </div>

                    {/* Progress Notes */}
                    {item.collaboration.progress_notes && (
                      <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-700">{item.collaboration.progress_notes}</p>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-2 ml-4">
                    {item.collaboration.status === 'pending' && (
                      <div className="flex flex-col gap-2">
                        <div className="p-3 bg-amber-50 border-2 border-amber-300 rounded-lg mb-2">
                          <p className="text-xs font-semibold text-amber-900 mb-1">⚠️ Contract Required</p>
                          <p className="text-xs text-amber-800">Formalize this collaboration with contracts before starting work.</p>
                        </div>
                        <Button
                          size="sm"
                          className="btn-snow"
                          onClick={() => {
                            setViewingCollab(item);
                            setActiveTab('contract');
                          }}
                        >
                          <FileText className="w-4 h-4 mr-2" />
                          Create Contracts
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setViewingCollab(item);
                            setActiveTab('overview');
                          }}
                        >
                          View Details
                        </Button>
                      </div>
                    )}
                    {item.collaboration.status === 'in_progress' && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedCollab(item);
                            setUpdateData({
                              progress_status: item.collaboration.progress_status || '',
                              progress_notes: item.collaboration.progress_notes || ''
                            });
                            setShowUpdateModal(true);
                          }}
                        >
                          Update Progress
                        </Button>
                        <Button
                          size="sm"
                          className="btn-snow"
                          onClick={() => handleCompleteCollaboration(item.collaboration.id)}
                        >
                          Mark Completed
                        </Button>
                      </>
                    )}
                    {item.collaboration.status === 'completed' && (
                      item.has_rated ? (
                        <div className="flex items-center gap-1 px-3 py-1.5 bg-green-50 text-green-700 rounded-md border border-green-200">
                          <Star className="w-4 h-4 fill-green-500 text-green-500" />
                          <span className="text-sm font-medium">Rated ({item.my_rating?.score}/5)</span>
                        </div>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedCollab(item);
                            setShowRatingModal(true);
                          }}
                        >
                          <Star className="w-4 h-4 mr-1" />
                          Rate
                        </Button>
                      )
                    )}
                    <Button
                      size="sm"
                      className="btn-snow"
                      onClick={(e) => {
                        e.stopPropagation();
                        console.log('View Details clicked for:', item.collaboration.id);
                        setViewingCollab(item);
                        setActiveTab('overview');
                      }}
                    >
                      View Details
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Collaboration Details View */}
      {viewingCollab && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 9999 }}>
          <div className="bg-white rounded-lg w-full max-w-6xl max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="sticky top-0 bg-white border-b p-6 z-10">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-[#1B3240]">
                  {viewingCollab.project?.title}
                </h2>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setViewingCollab(null)}
                >
                  Close
                </Button>
              </div>
              
              {/* Tabs */}
              <div className="flex gap-2">
                <button
                  className={`px-4 py-2 rounded-t ${
                    activeTab === 'overview'
                      ? 'bg-[#62D84E] text-white'
                      : 'bg-gray-200 hover:bg-gray-300'
                  }`}
                  onClick={() => setActiveTab('overview')}
                >
                  Overview
                </button>
                <button
                  className={`px-4 py-2 rounded-t flex items-center ${
                    activeTab === 'contract'
                      ? 'bg-[#62D84E] text-white'
                      : 'bg-gray-200 hover:bg-gray-300'
                  }`}
                  onClick={() => setActiveTab('contract')}
                >
                  <FileText className="w-4 h-4 mr-1" />
                  Contract
                </button>
                <button
                  className={`px-4 py-2 rounded-t flex items-center ${
                    activeTab === 'messages'
                      ? 'bg-[#62D84E] text-white'
                      : 'bg-gray-200 hover:bg-gray-300'
                  }`}
                  onClick={() => setActiveTab('messages')}
                >
                  <MessageSquare className="w-4 h-4 mr-1" />
                  Communication
                </button>
              </div>
            </div>

            {/* Tab Content */}
            <div className="p-6">
              {activeTab === 'overview' && (
                <div className="space-y-4">
                  <Card>
                    <CardHeader>
                      <CardTitle>Collaboration Details</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <h4 className="font-medium mb-2">Project</h4>
                          <p>{viewingCollab.project?.title}</p>
                          <p className="text-sm text-gray-600">
                            {viewingCollab.pm_company?.name}
                          </p>
                        </div>
                        <div>
                          <h4 className="font-medium mb-2">Resource</h4>
                          <p>
                            {viewingCollab.resource?.first_name}{' '}
                            {viewingCollab.resource?.last_name}
                          </p>
                          <p className="text-sm text-gray-600">
                            {viewingCollab.rm_company?.name}
                          </p>
                        </div>
                        <div>
                          <h4 className="font-medium mb-2">Status</h4>
                          {getStatusBadge(viewingCollab.collaboration.status)}
                        </div>
                        <div>
                          <h4 className="font-medium mb-2">Progress</h4>
                          <p>{viewingCollab.collaboration.progress_status || 'Not set'}</p>
                        </div>
                        <div>
                          <h4 className="font-medium mb-2">Commitment</h4>
                          <p>{viewingCollab.collaboration.commitment_percentage}%</p>
                        </div>
                        <div>
                          <h4 className="font-medium mb-2">Timeline</h4>
                          <p className="text-sm">
                            Started: {viewingCollab.collaboration.start_date || 'Not started'}
                          </p>
                          {viewingCollab.collaboration.estimated_end_date && (
                            <p className="text-sm">
                              Est. End: {viewingCollab.collaboration.estimated_end_date}
                            </p>
                          )}
                        </div>
                      </div>
                      {viewingCollab.collaboration.progress_notes && (
                        <div className="mt-4">
                          <h4 className="font-medium mb-2">Progress Notes</h4>
                          <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded">
                            {viewingCollab.collaboration.progress_notes}
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}

              {activeTab === 'contract' && (
                <ContractWorkflowSection
                  collaboration={viewingCollab}
                  onContractSigned={() => {
                    fetchCollaborations();
                    setViewingCollab(null);
                  }}
                  userRole={user?.role}
                />
              )}

              {activeTab === 'messages' && (
                <CollaborationMessages
                  collaboration={viewingCollab}
                  currentUserId={user?.id}
                />
              )}
            </div>
          </div>
        </div>
      )}
      </main>

      {/* Update Progress Modal */}
      {showUpdateModal && selectedCollab && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-lg mx-4">
            <CardHeader>
              <CardTitle>Update Progress</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Progress Status</Label>
                <Select
                  value={updateData.progress_status}
                  onValueChange={(value) => setUpdateData({ ...updateData, progress_status: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select progress..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0%">0% - Just Started</SelectItem>
                    <SelectItem value="25%">25% - In Progress</SelectItem>
                    <SelectItem value="50%">50% - Halfway</SelectItem>
                    <SelectItem value="75%">75% - Nearly Done</SelectItem>
                    <SelectItem value="100%">100% - Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Progress Notes (Optional)</Label>
                <Textarea
                  rows={4}
                  placeholder="Add notes about progress, milestones, or updates..."
                  value={updateData.progress_notes}
                  onChange={(e) => setUpdateData({ ...updateData, progress_notes: e.target.value })}
                />
              </div>

              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setShowUpdateModal(false)}>
                  Cancel
                </Button>
                <Button className="btn-snow" onClick={handleUpdateProgress}>
                  Save Update
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Rating Modal */}
      {showRatingModal && selectedCollab && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-lg mx-4">
            <CardHeader>
              <CardTitle>Rate {isPM ? 'Resource' : 'Company'}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-600">
                Your rating will be anonymous and help others make better decisions.
              </p>

              <div>
                <Label>Rating (1-5 stars) *</Label>
                <div className="flex gap-2 mt-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      onClick={() => setRatingData({ ...ratingData, score: star })}
                      className="focus:outline-none"
                    >
                      <Star
                        className={`w-8 h-8 ${
                          star <= ratingData.score
                            ? 'fill-yellow-400 text-yellow-400'
                            : 'text-gray-300'
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label>Comment (Optional)</Label>
                <Textarea
                  rows={4}
                  placeholder="Share your experience working together..."
                  value={ratingData.comment}
                  onChange={(e) => setRatingData({ ...ratingData, comment: e.target.value })}
                />
              </div>

              {/* Ratings Disclaimer */}
              <p className="text-xs text-gray-500">
                Ratings reflect user feedback and do not constitute verification or endorsement by SnowExchange.
              </p>

              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => {
                  setShowRatingModal(false);
                  setRatingData({ score: 0, comment: '' });
                }}>
                  Cancel
                </Button>
                <Button className="btn-snow" onClick={handleSubmitRating}>
                  Submit Rating
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default CollaborationsPage;
